/**
 * ETL block — the ReactFlow custom node (ADR-0038, P4).
 *
 * Renders a descriptor-driven block: category-tinted header (icon + label),
 * a live status ring, and input/output handles (one row per port, labelled).
 * Dynamic ports (router channels, etc.) come from `data.inputs/outputs` which
 * the editor recomputes on config change — `useUpdateNodeInternals` tells
 * ReactFlow to re-measure the handles.
 */
import * as React from "react";
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from "reactflow";
import {
  ArrowRightLeft,
  Boxes,
  Code2,
  Database,
  Filter,
  GitFork,
  Globe,
  Layers,
  LayoutGrid,
  ListTree,
  Merge,
  PauseCircle,
  Plus,
  Replace,
  ShieldCheck,
  Sigma,
  Table,
  TableProperties,
  Terminal,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/lib/cn";

import type { EtlNodeData } from "./etlGraph";

const ICONS: Record<string, LucideIcon> = {
  database: Database,
  "database-search": Database,
  "database-export": Database,
  table: Table,
  "table-plus": Table,
  globe: Globe,
  filter: Filter,
  "git-fork": GitFork,
  "git-merge": Merge,
  "arrow-merge": Merge,
  "arrows-exchange": ArrowRightLeft,
  "list-tree": ListTree,
  "copy-off": Layers,
  "sort-ascending": Layers,
  sigma: Sigma,
  code: Code2,
  terminal: Terminal,
  "shield-check": ShieldCheck,
  "file-json": Code2,
  "file-spreadsheet": Table,
  plus: Plus,
  replace: Replace,
  "table-share": TableProperties,
  "layout-grid": LayoutGrid,
};

function iconFor(name: string): LucideIcon {
  return ICONS[name] ?? Boxes;
}

const PORT_ROW = "relative flex h-[22px] items-center";

export function EtlBlockNode({ id, data, selected }: NodeProps<EtlNodeData>) {
  const updateInternals = useUpdateNodeInternals();
  const sig = JSON.stringify({
    i: data.inputs.map((p) => p.id),
    o: data.outputs.map((p) => p.id),
  });
  React.useEffect(() => {
    updateInternals(id);
  }, [id, sig, updateInternals]);

  const Icon = iconFor(data.descriptor?.ui.icon ?? "");
  const accent = data.descriptor?.ui.color ?? "#64748b";
  const maxRows = Math.max(data.inputs.length, data.outputs.length);

  const ring =
    data.runStatus === "running"
      ? "ring-2 ring-emerald-400 shadow-[0_0_16px_rgba(74,222,128,0.55)] animate-pulse"
      : data.runStatus === "failed"
        ? "ring-2 ring-danger shadow-[0_0_16px_rgba(225,29,72,0.5)]"
        : data.runStatus === "done"
          ? "ring-2 ring-emerald-500/70"
          : selected
            ? "ring-2 ring-accent"
            : "";

  return (
    <div
      className={cn(
        "w-[210px] overflow-hidden rounded-lg border border-border/70 bg-surface text-foreground shadow-sm",
        ring,
      )}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-3 py-2"
        style={{ background: accent, color: "white" }}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[10px] uppercase tracking-wide opacity-80">
            {data.descriptor?.ui.label ?? data.blockType}
          </div>
          <div className="truncate text-sm font-medium leading-tight">{data.label}</div>
        </div>
        {/* Buffer-all badge: this is a BLOCKING operator (sort/aggregate/join)
            — it reads the whole input before emitting, so downstream stalls
            until it finishes. Explains the "sink at 0 while the pipe streams". */}
        {data.descriptor?.bufferAll && (
          <span
            className="flex shrink-0 items-center gap-0.5 rounded bg-black/25 px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wide"
            title="Operatore bloccante: bufferizza tutto l'input prima di emettere — il flusso a valle si ferma finché non finisce."
          >
            <PauseCircle className="h-3 w-3" /> buffer
          </span>
        )}
      </div>

      {/* Ports — one full-width relative row per (input?, output?) pair. The
          row is the Handle's positioned ancestor; ReactFlow's default
          left:0/right:0 + translate(±50%) straddles the node's left/right
          border, vertically centred on the row (NO manual offsets). */}
      {maxRows > 0 && (
        <div className="border-t border-border/50 py-1.5">
          {Array.from({ length: maxRows }).map((_, i) => {
            const input = data.inputs[i];
            const output = data.outputs[i];
            const danger = output?.id === "deadletter";
            return (
              <div key={i} className={cn(PORT_ROW, "justify-between px-3")}>
                {input && (
                  <Handle
                    type="target"
                    position={Position.Left}
                    id={input.id}
                    className="!h-2.5 !w-2.5 !border-2 !border-surface !bg-foreground"
                  />
                )}
                <span className="truncate text-[10px] text-muted-foreground">{input?.label ?? ""}</span>
                <span
                  className={cn(
                    "truncate text-[10px]",
                    danger ? "text-danger" : "text-muted-foreground",
                  )}
                >
                  {output?.label ?? ""}
                </span>
                {output && (
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={output.id}
                    className={cn(
                      "!h-2.5 !w-2.5 !border-2 !border-surface",
                      danger ? "!bg-danger" : "!bg-foreground",
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Live metrics (run overlay) — IN / OUT / dead-letter / duration. */}
      {data.metrics && (
        <div className="grid grid-cols-4 gap-1 border-t border-border/50 bg-muted/30 px-2 py-1 text-center">
          {(
            [
              ["IN", data.metrics.items_in ?? 0, ""],
              ["OUT", data.metrics.items_out ?? 0, ""],
              ["DL", data.metrics.dead_lettered ?? 0, (data.metrics.dead_lettered ?? 0) > 0 ? "text-danger" : ""],
              ["ms", data.metrics.duration_ms ?? 0, ""],
            ] as const
          ).map(([label, value, tone]) => (
            <div key={label}>
              <div className="text-[8px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
              <div className={cn("text-[10px] font-medium tabular-nums", tone)}>{value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
